/**
 * Where things in the trash were: each row's breadcrumb as it was when deleted (its parts narrow the trash to that
 * folder, gone or not), a folder deleted with what was in it, and the folder filter (with "Include subfolders").
 */
import type { SpaceOut, TrashRow } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { crumbsOf, pathText, useFolderIndex } from "../../lib/folders.ts";
import { location, setQuery } from "../../lib/router.ts";
import { Select, Switch } from "../../ui/form.tsx";
import { Breadcrumb } from "../folders/breadcrumb.tsx";

/** A row's place when it was deleted (its parts filter the trash), and, a folder, how much went with it. */
export function TrashPlace({ space, row }: { space: SpaceOut; row: TrashRow }) {
  const contents = row.object === "folder" ? (row.contents ?? 0) : 0;
  return (
    <>
      {contents > 0 ? <span>{t("trash.withContents", { count: contents })}</span> : null}
      <Breadcrumb
        space={space}
        path={row.folder_path}
        small={true}
        onPick={(id) => setQuery({ folder: id, deep: null, cursor: null })}
      />
    </>
  );
}

/**
 * Which folder the trash shows (anywhere, the space's folders, or one picked from a row, gone perhaps), and whether
 * what was in its subfolders is in it too. `rows`: the page's, naming a folder picked from one.
 */
export function TrashFolderFilter({ space, rows }: { space: SpaceOut; rows: readonly TrashRow[] }) {
  const sp = location.value.searchParams;
  const index = useFolderIndex(space);
  const folder = sp.get("folder") ?? "";
  const live = (index.data?.rows ?? []).map((f) => ({
    value: f.id,
    label: pathText(crumbsOf(index.data, f.id)),
  }));
  // A folder picked from a row and not among the live ones (deleted too): named as the row says it.
  const named = rows.flatMap((r) => r.folder_path).find((c) => c.id === folder);
  const picked = folder && !live.some((o) => o.value === folder) ? [{ value: folder, label: named?.name ?? "…" }] : [];
  // A space without folders (nothing in the trash was in one either) has nothing to pick.
  if (!(folder || live.length > 0 || rows.some((r) => r.folder_path.length > 0 || r.object === "folder"))) {
    return null;
  }
  return (
    <span class="flex shrink-0 items-center gap-3">
      <span class="w-48">
        <Select
          aria-label={t("trash.folder")}
          value={folder}
          onValue={(v) => setQuery({ folder: v || null, deep: null, cursor: null })}
          options={[{ value: "", label: t("trash.anyFolder") }, ...picked, ...live]}
        />
      </span>
      {folder ? (
        <Switch
          checked={sp.get("deep") === "true"}
          label={t("folders.deep")}
          onCheck={(on) => setQuery({ deep: on ? "true" : null, cursor: null })}
        />
      ) : null}
    </span>
  );
}
